// Collapsible right-side library of all registered widgets. Multi widgets
// show a count badge and clicking adds another instance; singletons show
// a checkmark and toggle. Pinned widgets are hidden from the library.

import React, { useMemo } from 'react';
import { WIDGET_REGISTRY } from './widgetRegistry';

function CockpitLibrary({ availableIds, countsByType, onToggle, open, onToggleOpen }) {
  const grouped = useMemo(() => {
    const map = new Map();
    availableIds.forEach((id) => {
      const def = WIDGET_REGISTRY[id];
      if (!def || def.pinned) return;
      const cat = def.category || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(def);
    });
    return Array.from(map.entries());
  }, [availableIds]);

  const counts = countsByType || {};

  // Counter shows total instances on canvas vs total widget types available
  // (excluding pinned). For multi widgets one type can contribute many.
  const toggleableTotal = useMemo(
    () => availableIds.filter((id) => !WIDGET_REGISTRY[id]?.pinned).length,
    [availableIds]
  );
  const toggleableVisible = useMemo(
    () => Object.entries(counts).reduce((sum, [type, n]) => (
      WIDGET_REGISTRY[type]?.pinned ? sum : sum + n
    ), 0),
    [counts]
  );

  return (
    <aside className={`cockpit-library ${open ? 'open' : 'closed'}`}>
      <button
        type="button"
        className="cockpit-library-toggle"
        onClick={onToggleOpen}
        title={open ? 'Hide widget library' : 'Show widget library'}
        aria-label="Toggle widget library"
      >
        {open ? '›' : '‹'}
      </button>
      {open && (
        <div className="cockpit-library-body">
          <div className="cockpit-library-header">
            <span className="cockpit-library-title">Widget Library</span>
            <span className="cockpit-library-meta">{toggleableVisible}/{toggleableTotal}</span>
          </div>
          {grouped.map(([category, items]) => (
            <div key={category} className="cockpit-library-group">
              <div className="cockpit-library-group-name">{category}</div>
              {items.map((def) => {
                const count = counts[def.id] || 0;
                const isOn = count > 0;
                const isMulti = !!def.multi;
                return (
                  <button
                    key={def.id}
                    type="button"
                    className={`cockpit-library-item ${isOn ? 'on' : ''} ${isMulti ? 'multi' : ''}`}
                    onClick={() => onToggle(def.id)}
                    title={isMulti ? `${def.description} (click to add new instance)` : def.description}
                  >
                    <span className="cockpit-library-item-check">
                      {isMulti ? (count > 0 ? `×${count}` : '+') : (isOn ? '✓' : '+')}
                    </span>
                    <span className="cockpit-library-item-text">
                      <span className="cockpit-library-item-title">{def.title}</span>
                      {def.description && (
                        <span className="cockpit-library-item-desc">{def.description}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}

export default CockpitLibrary;
